import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { Ref } from 'vue'
import type { Company } from '@/types/domain/Company'
import type { User } from '@/types/domain/User'
import type { UserDisplayMode } from '@/constants/enums/UserDisplayMode'

/**
 * Types
 */
type AuthStateTree = {
  company: Ref<Company | undefined>
  user: Ref<User | undefined>
  userDisplayMode: Ref<UserDisplayMode | undefined>
  setCompany: (company: Company) => void
  setUser: (user: User) => void
  setUserDisplayMode: (newUserDisplayMode: UserDisplayMode) => void
}

/**
 * Composable
 */
export const useAuthStore = defineStore<'auth', AuthStateTree>('auth', () => {
  const company = ref<Company | undefined>()
  const user = ref<User | undefined>()
  const userDisplayMode = ref<UserDisplayMode | undefined>()

  const setCompany = (newCompany: Company) => {
    company.value = newCompany
  }
  const setUser = (newUser: User) => {
    user.value = newUser
  }
  const setUserDisplayMode = (newUserDisplayMode: UserDisplayMode) => {
    userDisplayMode.value = newUserDisplayMode
  }

  return {
    company,
    user,
    userDisplayMode,
    setCompany,
    setUser,
    setUserDisplayMode,
  }
})

/**
 * Utils
 */
type AuthStore = ReturnType<typeof useAuthStore>

export const isAuthStore = (
  store: unknown,
): store is ReturnType<typeof useAuthStore> => {
  return (
    !!store
    && (store as AuthStore).company !== undefined
    && (store as AuthStore).user !== undefined
  )
}
